import moment from 'moment';

import { apiServiceUrl } from '../constants';
import { LOGIN_PATH, SHARED_VALUE_PROPOSITION_PATH, VP_SHARE_LINK_PATH } from '../constants/pagePath';
import { fetchJSONAsync } from './api';
import { buildSocialShareLinks } from './profileShare';

// Share Link helpers for a Value Proposition. A Share Link is the public, resettable
// /vp/shared/{shareKey} address of a Public VP; it is not the Invite Link (sharedKey)
// carried by Invite emails and VPX lists.

export const VP_SHARE_TEXT = 'Check out this value proposition on PROVE IT TO ME™';
export const VP_SHARE_EMAIL_SUBJECT = 'A value proposition on PROVE IT TO ME™';

// Every Via: how a View arrived, as the server stores it. Share-Link channels tag
// their URL with ?via= so the server can attribute the View; the QR code is tagged
// via=qr by the server itself (GetVpQrCode), and in-app VPX lists tag their Invite
// Link with via=vpx. `label` names the Via in View counts: brand names keep their
// casing, plain words stay lowercase ("7 LinkedIn · 3 invite · 2 QR"). The table's
// order is the tie-break when two Vias have the same count. `social` marks the
// Share-Link channels offered as share buttons (their value is the channel key
// ShareLinkControls draws a button for).
export const VP_VIAS = [
  { value: 'link', label: 'link' },
  { value: 'linkedin', label: 'LinkedIn', social: true },
  { value: 'x', label: 'X', social: true },
  { value: 'whatsapp', label: 'WhatsApp', social: true },
  { value: 'email', label: 'email', social: true },
  { value: 'qr', label: 'QR' },
  { value: 'invite', label: 'invite' },
  { value: 'vpx', label: 'VPX' },
  { value: 'direct', label: 'direct' },
];

const viaNamed = (value) => VP_VIAS.find((via) => via.value === value).value;

// The Via the copyable Share Link carries.
export const VP_SHARE_LINK_VIA = viaNamed('link');

// The Share-Link channels offered as share buttons, in table order.
export const VP_SOCIAL_VIAS = VP_VIAS.filter((via) => via.social);

export const buildVpSharePath = (shareKey) => VP_SHARE_LINK_PATH.replace(':shareKey', shareKey);

export const buildVpShareUrl = (shareKey, via, origin) => {
  const url = `${origin ?? window.location.origin}${buildVpSharePath(shareKey)}`;
  return via ? `${url}?via=${encodeURIComponent(via)}` : url;
};

export const buildVpQrUrl = (shareKey, apiBase = apiServiceUrl) =>
  `${apiBase}/ValueProposition/GetVpQrCode?shareKey=${shareKey}`;

// One share link per social channel, each tagged with its own via (the channel's
// key in buildSocialShareLinks is its Via value).
export const buildVpSocialShareLinks = (shareKey, origin) =>
  Object.fromEntries(
    VP_SOCIAL_VIAS.map(({ value }) => [
      value,
      buildSocialShareLinks(buildVpShareUrl(shareKey, value, origin), VP_SHARE_TEXT, VP_SHARE_EMAIL_SUBJECT)[value],
    ])
  );

// The two shared-VP reads. `via` comes straight from the address and is passed
// through unchanged: the server whitelists it (Share Key: link|linkedin|x|whatsapp|
// email|qr, else direct; Invite key: vpx, else invite).
const withVia = (url, via) => (via ? `${url}&via=${encodeURIComponent(via)}` : url);

export const buildVpShareReadUrl = (shareKey, via, apiBase = apiServiceUrl) =>
  withVia(`${apiBase}/ValueProposition/GetValuePropositionByShareKey?shareKey=${shareKey}`, via);

export const buildVpInviteReadUrl = (sharedKey, via, apiBase = apiServiceUrl) =>
  withVia(`${apiBase}/ValueProposition/GetValuePropositionBySharedKey?sharedKey=${sharedKey}`, via);

// In-app VPX lists open a VP through its Invite Link; via=vpx attributes the View.
export const VP_INVITE_VIA_VPX = viaNamed('vpx');

export const buildVpxInviteLocation = (sharedKey) => ({
  pathname: SHARED_VALUE_PROPOSITION_PATH.replace(':sharedKey', sharedKey),
  search: `?via=${VP_INVITE_VIA_VPX}`,
});

// "Sign in to follow" on a shared VP: login, then back to the same address
// (query included, so the read keeps its via).
export const buildSignInRedirect = (returnTo) =>
  `${LOGIN_PATH}?redirectUrl=${encodeURIComponent(returnTo)}`;

// View counts (owner only).
export const VP_NOT_OPENED_YET = 'Not opened yet';

const viaRank = (value) => {
  const index = VP_VIAS.findIndex((via) => via.value === value);
  return index === -1 ? VP_VIAS.length : index;
};
const viaLabel = (value) => VP_VIAS.find((via) => via.value === value)?.label ?? value;

export const formatLastViewed = (lastViewedAt, now = Date.now()) => {
  if (!lastViewedAt) return '';
  const viewed = moment(lastViewedAt);
  return viewed.isValid() ? viewed.from(moment(now)) : '';
};

/** "Opened 12 times · last viewed 2 days ago", or "Not opened yet". */
export const formatVpViewSummary = (viewCount, lastViewedAt, now = Date.now()) => {
  const count = Number(viewCount) || 0;
  if (count <= 0) return VP_NOT_OPENED_YET;
  const opened = `Opened ${count} ${count === 1 ? 'time' : 'times'}`;
  const lastViewed = formatLastViewed(lastViewedAt, now);
  return lastViewed ? `${opened} · last viewed ${lastViewed}` : opened;
};

/** "7 LinkedIn · 3 invite · 2 QR": largest first; empty when there are no Views. */
export const formatVpViewsByVia = (viewsByVia) =>
  Object.entries(viewsByVia ?? {})
    .filter(([, count]) => Number(count) > 0)
    .sort(([viaA, a], [viaB, b]) => b - a || viaRank(viaA) - viaRank(viaB))
    .map(([via, count]) => `${count} ${viaLabel(via)}`)
    .join(' · ');

/**
 * Reset Link: POST ResetShareKey and resolve to the new Share Key. Rejects on any
 * refusal (403 VP Sharing off, 401 not signed in / not the owner, 400 unknown VP,
 * 503 maintenance mode) or a 200 without a key.
 */
export const resetVpShareKey = async (vpId, token, apiBase = apiServiceUrl) => {
  const response = await fetchJSONAsync(`${apiBase}/ValueProposition/ResetShareKey`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify({ valuePropositionId: vpId }),
  });
  const shareKey = response.status === 200 ? response.data?.shareKey : null;
  if (!shareKey) {
    // e.g. a limited VP: HTTP 400 with { status: 403, message }; VP Sharing off: 403 with a message.
    const serverMessage = typeof response.data?.message === 'string' && response.data.message.trim()
      ? response.data.message.trim()
      : null;
    const error = new Error(serverMessage || `Reset Link failed (${response.status})`);
    error.serverMessage = serverMessage;
    throw error;
  }
  return shareKey;
};
